import { RemediationProfile } from 'src/app/models/remediation-profile/remediation-profile';
import { Remediation } from 'src/app/models/remediation-profile/remediation';
import { LoggingChannels, LoggingService } from '../logging/logging.service';
import { FeatureSwitchService } from '../feature-switch/feature-switch.service';
import { VirtualBackgroundService } from '../virtual-background/virtual-background.service';

export class RemediationUpgradeDowngrade {

  constructor(
    private logging: LoggingService,
    private featureSwitch: FeatureSwitchService,
    private background: VirtualBackgroundService,
  ) {}

  upgrade(
    participant: string,
    sharing: boolean,
    participantRemediations: Remediation,
    shareRemediations: Remediation,
    savedProfiles: { [key: string]: RemediationProfile },
    upgradeTime: number,
    setLocalProfile: (participant: string, profile: RemediationProfile, sharing: boolean) => void,
    logRemediation: (participant: string, remediation: string, profile: RemediationProfile, sharing: boolean) => void,
    applyRemediations: (participant: string, sharing: boolean) => void,
  ): void {
    let remediation: Remediation;
    let forceProfile: string;
    if (sharing) {
      remediation = shareRemediations;
      forceProfile = window.forceShareProfile;
    } else {
      if (savedProfiles) {
        return;
      }
      remediation = participantRemediations;
      forceProfile = window.forceProfile;
    }

    if (this.featureSwitch.debugProfile && forceProfile) {
      return this.debugProfile(
        participant, sharing, participantRemediations, shareRemediations,
        setLocalProfile, applyRemediations,
      );
    }

    if (this.background.isActive && this.background.resolution < 0.5 && !sharing) {
      return;
    }

    if (!remediation.profiles) {
      return;
    }

    const now = Date.now();
    if (now - remediation.last[participant] < upgradeTime) {
      return;
    }

    remediation.last[participant] = now;
    let found = false;
    for (let i = remediation.profiles.length; i--; i >= 0) {
      const profile = remediation.profiles[i];
      if (found && profile.video.width <= remediation.max.width && profile.video.height <= remediation.max.height) {
        setLocalProfile(participant, profile, sharing);
        logRemediation(participant, 'Upgrade profile', profile, sharing);
        this.logging.log('IssueRemediationService: upgrade profile', LoggingChannels.Remediation, participant, profile.id, sharing);
        applyRemediations(participant, sharing);
        break;
      }
      if (profile.id === remediation.local[participant].id) {
        found = true;
      }
    }
  }

  downgrade(
    participant: string,
    sharing: boolean,
    participantRemediations: Remediation,
    shareRemediations: Remediation,
    savedProfiles: { [key: string]: RemediationProfile },
    downgradeTime: number,
    setLocalProfile: (participant: string, profile: RemediationProfile, sharing: boolean) => void,
    logRemediation: (participant: string, remediation: string, profile: RemediationProfile, sharing: boolean) => void,
    applyRemediations: (participant: string, sharing: boolean) => void,
    display: (notification: { notification: boolean; message: string; color: string }) => void,
  ): void {
    let remediation: Remediation;
    let forceProfile: string;
    if (sharing) {
      remediation = shareRemediations;
      forceProfile = window.forceShareProfile;
    } else {
      if (savedProfiles) {
        return;
      }
      remediation = participantRemediations;
      forceProfile = window.forceProfile;
    }

    if (this.featureSwitch.debugProfile && forceProfile) {
      return;
    }

    if (!remediation.profiles) {
      return;
    }

    const now = Date.now();
    if (now - remediation.last[participant] < downgradeTime) {
      return;
    }

    remediation.last[participant] = now;
    let found = false;
    for (const profile of remediation.profiles) {
      if (found) {
        setLocalProfile(participant, profile, sharing);
        logRemediation(participant, 'Downgrade profile', profile, sharing);
        this.logging.log('IssueRemediationService: downgrade profile', LoggingChannels.Remediation, participant, profile.id, sharing);
        applyRemediations(participant, sharing);
        if (profile.id === remediation.profiles[remediation.profiles.length - 1].id) {
          display({
            notification: true,
            message: 'problems.video',
            color: 'warning',
          });
        }
        break;
      }
      if (profile.id === remediation.local[participant].id) {
        found = true;
      }
    }
  }

  debugProfile(
    participant: string,
    sharing: boolean,
    participantRemediations: Remediation,
    shareRemediations: Remediation,
    setLocalProfile: (participant: string, profile: RemediationProfile, sharing: boolean) => void,
    applyRemediations: (participant: string, sharing: boolean) => void,
  ): void {
    let remediation: Remediation;
    let forceProfile: string;
    if (sharing) {
      remediation = shareRemediations;
      forceProfile = window.forceShareProfile;
    } else {
      remediation = participantRemediations;
      forceProfile = window.forceProfile;
    }

    if (forceProfile !== remediation.local[participant].id) {
      for (const profile of remediation.profiles) {
        if (profile.id === forceProfile) {
          setLocalProfile(participant, profile, sharing);
          applyRemediations(participant, sharing);
        }
      }
    }
  }

  findNewProfile(id: string, sharing: boolean, participantRemediations: Remediation, shareRemediations: Remediation): RemediationProfile {
    let remediation: Remediation;
    let currentProfile: RemediationProfile;
    let remoteProfile: RemediationProfile;
    if (sharing) {
      remediation = shareRemediations;
      currentProfile = shareRemediations.local[id];
      remoteProfile = shareRemediations.remote[id];
    } else {
      remediation = participantRemediations;
      currentProfile = participantRemediations.local[id];
      remoteProfile = participantRemediations.remote[id];
    }

    if (!currentProfile || !remoteProfile) {
      return;
    }

    if (
      currentProfile.video.width < remoteProfile.video.width ||
      currentProfile.video.height < remoteProfile.video.height
    ) {
      return;
    }

    for (const profile of remediation.profiles) {
      if (
        profile.video.width <= remoteProfile.video.width &&
        profile.video.height <= remoteProfile.video.height &&
        profile.video.framerate <= remoteProfile.video.framerate
      ) {
        return profile;
      }
    }
  }
}
